/**
 * Admin Bookings Tab - Manage cat sitting bookings
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar, Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminBookingsTab = () => {
  const { bookings, loadData } = useAdmin();
  
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    customer_name: "", customer_email: "", cat_name: "", package_id: "daily",
    start_date: "", end_date: "", notes: "", status: "confirmed", payment_status: "paid", amount: ""
  });

  const resetBookingForm = () => {
    setBookingForm({
      customer_name: "", customer_email: "", cat_name: "", package_id: "daily",
      start_date: "", end_date: "", notes: "", status: "confirmed", payment_status: "paid", amount: ""
    });
    setEditingBooking(null);
    setShowBookingForm(false);
  };

  const handleSaveBooking = async () => {
    if (!bookingForm.customer_name || !bookingForm.start_date) {
      toast.error("Customer name and start date required");
      return;
    }
    try {
      const data = { ...bookingForm, amount: parseFloat(bookingForm.amount) || 0 };
      if (editingBooking) {
        await axios.put(`${API}/bookings/${editingBooking.booking_id}`, data, { withCredentials: true });
        toast.success("Booking updated");
      } else {
        await axios.post(`${API}/bookings/admin`, data, { withCredentials: true });
        toast.success("Booking created");
      }
      resetBookingForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save booking");
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setBookingForm({
      customer_name: booking.customer_name || "",
      customer_email: booking.customer_email || "",
      cat_name: booking.cat_name || "",
      package_id: booking.package_id || "daily",
      start_date: booking.start_date?.split("T")[0] || "",
      end_date: booking.end_date?.split("T")[0] || "",
      notes: booking.notes || "",
      status: booking.status || "confirmed",
      payment_status: booking.payment_status || "pending",
      amount: booking.amount?.toString() || ""
    });
    setShowBookingForm(true);
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { withCredentials: true });
      toast.success("Booking deleted");
      loadData();
    } catch { toast.error("Delete failed"); }
  };

  const getStatusColor = (status) => {
    const colors = { confirmed: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", cancelled: "bg-red-100 text-red-700", completed: "bg-blue-100 text-blue-700" };
    return colors[status] || "bg-stone-100 text-stone-700";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle><Calendar className="w-5 h-5 inline mr-2" />Bookings</CardTitle>
          <CardDescription>Manage cat sitting bookings</CardDescription>
        </div>
        <Button onClick={() => { resetBookingForm(); setShowBookingForm(true); }} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" /> Add Booking
        </Button>
      </CardHeader>
      <CardContent>
        {showBookingForm && (
          <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h3 className="font-semibold mb-4">{editingBooking ? "Edit Booking" : "Add Booking"}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Customer Name *</Label><Input value={bookingForm.customer_name} onChange={e => setBookingForm({...bookingForm, customer_name: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={bookingForm.customer_email} onChange={e => setBookingForm({...bookingForm, customer_email: e.target.value})} /></div>
              <div><Label>Cat Name</Label><Input value={bookingForm.cat_name} onChange={e => setBookingForm({...bookingForm, cat_name: e.target.value})} /></div>
              <div><Label>Package</Label>
                <Select value={bookingForm.package_id} onValueChange={v => setBookingForm({...bookingForm, package_id: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily Visit</SelectItem>
                    <SelectItem value="overnight">Overnight Stay</SelectItem>
                    <SelectItem value="weekly">Weekly Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Start Date *</Label><Input type="date" value={bookingForm.start_date} onChange={e => setBookingForm({...bookingForm, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={bookingForm.end_date} onChange={e => setBookingForm({...bookingForm, end_date: e.target.value})} /></div>
              <div><Label>Amount (£)</Label><Input type="number" step="0.01" value={bookingForm.amount} onChange={e => setBookingForm({...bookingForm, amount: e.target.value})} /></div>
              <div><Label>Status</Label>
                <Select value={bookingForm.status} onValueChange={v => setBookingForm({...bookingForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea value={bookingForm.notes} onChange={e => setBookingForm({...bookingForm, notes: e.target.value})} /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSaveBooking} className="bg-amber-600 hover:bg-amber-700">{editingBooking ? "Update" : "Create"} Booking</Button>
              <Button variant="outline" onClick={resetBookingForm}>Cancel</Button>
            </div>
          </div>
        )}
        
        {bookings.length === 0 ? (
          <p className="text-center text-stone-500 py-8">No bookings yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Cat</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map(booking => (
                <TableRow key={booking.booking_id}>
                  <TableCell>
                    <p className="font-medium">{booking.customer_name}</p>
                    <p className="text-xs text-stone-500">{booking.customer_email}</p>
                  </TableCell>
                  <TableCell>{booking.cat_name || "-"}</TableCell>
                  <TableCell className="capitalize">{booking.package_id?.replace("_", " ")}</TableCell>
                  <TableCell className="text-sm">
                    {booking.start_date?.split("T")[0]}
                    {booking.end_date && ` - ${booking.end_date?.split("T")[0]}`}
                  </TableCell>
                  <TableCell><Badge className={getStatusColor(booking.status)}>{booking.status}</Badge></TableCell>
                  <TableCell>£{booking.amount?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEditBooking(booking)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteBooking(booking.booking_id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBookingsTab;
